import { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Check, ArrowLeft } from "lucide-react";
import PageHead from "../components/PageHead";
import ParticleField from "../components/ParticleField";
import Reveal from "../components/Reveal";
import { PrimaryButton, GhostButton } from "../components/Buttons";
import { heroBgStyle } from "../lib/heroBg";
import { services } from "../data/site";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = useMemo(() => services.find((s) => s.id === slug), [slug]);

  if (!service) return <Navigate to="/services" replace />;

  const Icon = service.icon;
  const next = services[(services.findIndex((s) => s.id === slug) + 1) % services.length];

  return (
    <main data-testid={`service-detail-${slug}`}>
      <PageHead
        title={`${service.title} | Digital Archdeacon Media Ltd`}
        description={service.tagline}
      />

      {/* Hero — same bg image */}
      <section className="relative pt-32 pb-12 overflow-hidden" style={heroBgStyle}>
        <ParticleField density={40} />
        <div className="absolute inset-0 bg-grid opacity-25 pointer-events-none" />
        <div className="container-x relative">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-sm text-[#CBD5E1] hover:text-white mb-8"
            data-testid="service-back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            All services
          </Link>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <div>
                <div className="inline-flex items-center gap-3 rounded-full border border-electric/30 bg-electric/10 pl-2 pr-4 py-1.5">
                  <span className="grid place-items-center h-7 w-7 rounded-full bg-electric/20 text-electric">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="text-xs uppercase tracking-[0.2em] text-electric font-medium">
                    Service
                  </span>
                </div>
                <h1 className="h-display text-5xl md:text-6xl lg:text-7xl mt-5">
                  {service.title}
                </h1>
                <p className="mt-6 text-lg md:text-xl text-[#CBD5E1] leading-relaxed">
                  {service.tagline}
                </p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <PrimaryButton to="/request-quote" testId="service-detail-cta-quote">
                    Get a Free Quote
                  </PrimaryButton>
                  <GhostButton to="/contact" testId="service-detail-cta-contact">
                    Talk to us first
                  </GhostButton>
                </div>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="relative aspect-[4/3] rounded-[28px] overflow-hidden border border-white/10 shadow-[0_0_40px_rgba(30,111,255,0.15)]">
                <img src={service.image} alt={service.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F2C] via-transparent to-transparent" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Why this matters */}
      <section className="section-pad section-light-blue" data-testid="service-why">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <Reveal className="lg:col-span-4">
              <span className="text-xs uppercase tracking-[0.3em] text-electric font-semibold">Why this matters</span>
              <h2 className="h-display text-4xl md:text-5xl text-[#0A0F2C] mt-4">
                The real cost of getting this wrong.
              </h2>
            </Reveal>
            <Reveal className="lg:col-span-8" delay={120}>
              <p className="text-lg leading-relaxed text-[#1E293B]">{service.why}</p>
              <p className="mt-5 text-lg leading-relaxed text-[#475569]">{service.long}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Offerings */}
      <section className="section-pad" data-testid="service-offerings">
        <div className="container-x">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">What we deliver</span>
            <h2 className="h-display text-4xl md:text-5xl mt-4 text-white">Everything under one roof.</h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {service.offerings.map((o, i) => (
              <Reveal key={o.title} delay={i * 80}>
                <article className="glass p-7 h-full">
                  <p className="text-xs uppercase tracking-[0.18em] text-electric font-semibold">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="font-heading text-2xl font-semibold mt-3">{o.title}</h3>
                  <p className="mt-3 text-[#CBD5E1] leading-relaxed">{o.body}</p>
                </article>
              </Reveal>
            ))}
          </div>

          <div className="mt-14 grid lg:grid-cols-2 gap-12">
            <Reveal>
              <div className="glass p-8">
                <h3 className="font-heading text-xl font-semibold mb-5">Typical deliverables</h3>
                <ul className="space-y-3">
                  {service.deliverables.map((d) => (
                    <li key={d} className="flex items-start gap-3 text-[15px]">
                      <span className="mt-1 grid place-items-center h-5 w-5 rounded-full bg-electric/15 text-electric">
                        <Check className="h-3 w-3" />
                      </span>
                      <span className="text-white/90">{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="glass p-8">
                <h3 className="font-heading text-xl font-semibold mb-5">Our process</h3>
                <ol className="space-y-5">
                  {service.process.map((p) => (
                    <li key={p.step} className="grid grid-cols-[auto_1fr] gap-4">
                      <span className="font-heading font-bold text-electric text-2xl">{p.step}</span>
                      <div>
                        <p className="font-semibold text-white">{p.title}</p>
                        <p className="text-[#CBD5E1] text-sm mt-1 leading-relaxed">{p.body}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Unique CTA */}
      <section className="section-pad" data-testid="service-cta">
        <div className="container-x">
          <Reveal>
            <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-cta-gradient p-12 md:p-16">
              <div className="absolute -top-32 -right-20 h-80 w-80 rounded-full bg-electric/40 blur-3xl" />
              <div className="absolute -bottom-32 -left-24 h-80 w-80 rounded-full bg-gold/20 blur-3xl" />
              <div className="relative">
                <h2 className="h-display text-4xl md:text-5xl">
                  Ready to start your {service.title.toLowerCase()} project?
                </h2>
                <p className="mt-5 text-lg text-[#CBD5E1] max-w-2xl">
                  Tell us what you need. We'll come back with a tailored proposal in 24 hours.
                </p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <PrimaryButton to="/request-quote" testId="service-cta-quote-btn">
                    Get a Free Quote
                  </PrimaryButton>
                  <Link
                    to={`/services/${next.id}`}
                    className="inline-flex items-center gap-2 text-white font-semibold hover:text-gold transition-colors"
                    data-testid="service-next-link"
                  >
                    Next service: {next.title}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
